import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Title2,
  Text,
  Button,
  Spinner,
  Dropdown,
  Option,
  Badge,
  Card,
  Caption1,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PlayRegular, ArrowClockwise16Regular } from '@fluentui/react-icons';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { createTmdsClient } from '../../services/tmds';
import { ExpandableTable } from '../../components/ExpandableTable';
import type { Column } from '../../components/ExpandableTable';

const TASKS = [
  'apipath-testing',
  'capabilities-testing',
  'example-testing',
  'failure-testing',
] as const;

type TaskName = typeof TASKS[number];

interface JobRecord {
  id?: string;
  jobId?: string;
  task?: string;
  status?: string;
  createdAt?: string;
  completedAt?: string;
  durationSeconds?: number | null;
  summary?: { total?: number; passed?: number; failed?: number; passRate?: number } | null;
  [k: string]: unknown;
}

interface LatestResult {
  task: string;
  updatedAt?: string;
  resultCount?: number;
  results?: Array<Record<string, unknown>>;
}

interface DeploymentResult {
  deploymentName?: string;
  capability?: string;
  status?: string;
  httpStatus?: number;
  responseTimeMs?: number;
  error?: string | null;
  [k: string]: unknown;
}

interface ResultRow {
  apiPath?: string;
  deployment?: string;
  capability?: string;
  status?: string;
  summary?: { total?: number; healthy?: number; failed?: number; healthPercent?: number };
  deployments?: DeploymentResult[];
  [k: string]: unknown;
}

const useStyles = makeStyles({
  container: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  section: {
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
  },
  runControls: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end' },
  statusBadge: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  blockLabel: { display: 'block', marginBottom: tokens.spacingVerticalXS },
  blockLabelM: { display: 'block', marginBottom: tokens.spacingVerticalS },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalS },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: tokens.spacingVerticalM },
  dropdownTask: { minWidth: '220px', '@media (max-width: 480px)': { minWidth: '100%' } },
  runMessageRow: { marginTop: tokens.spacingVerticalS },
  cardContent: { marginTop: tokens.spacingVerticalXS },
  mutedText: { color: tokens.colorNeutralForeground3 },
  mutedBlock: { color: tokens.colorNeutralForeground3, display: 'block' },
  detailPanel: { marginBottom: tokens.spacingVerticalL, padding: tokens.spacingVerticalM, background: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium },
  buttonGroup: { display: 'flex', gap: tokens.spacingHorizontalS },
  deploymentWrapper: { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalS} ${tokens.spacingHorizontalXXL}`, background: tokens.colorNeutralBackground2 },
});

function statusColor(status?: string): 'success' | 'danger' | 'warning' | 'informative' {
  const s = (status ?? '').toLowerCase();
  if (s === 'completed') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'running' || s === 'pending') return 'warning';
  return 'informative';
}

function formatDuration(durationSeconds?: number | null): string {
  if (durationSeconds == null || durationSeconds <= 0) return '-';
  if (durationSeconds < 60) return `${durationSeconds.toFixed(1)}s`;
  if (durationSeconds < 3600) return `${(durationSeconds / 60).toFixed(1)}m`;
  return `${(durationSeconds / 3600).toFixed(1)}h`;
}

export default function TaskSuitePage() {
  const styles = useStyles();
  const { getToken } = useMsalAuth();
  const client = useMemo(() => createTmdsClient(getToken), [getToken]);
  const qc = useQueryClient();

  // Run tests state
  const [selectedTask, setSelectedTask] = useState<TaskName>(TASKS[0]);
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);
  const [runJobId, setRunJobId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // URL-driven task filter
  const [searchParams, setSearchParams] = useSearchParams();
  const detailTask = (searchParams.get('task') as TaskName | null) ?? null;
  const setDetailTask = useCallback((task: TaskName | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (task) next.set('task', task); else next.delete('task');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Job history query
  const jobsQuery = useQuery<{ jobs?: JobRecord[] }>({
    queryKey: ['task-suite-jobs'],
    queryFn: () => client.getTaskSuiteJobs<{ jobs?: JobRecord[] }>(),
    staleTime: 30_000,
  });

  const jobs = useMemo(() => {
    const raw = jobsQuery.data;
    if (Array.isArray(raw)) return raw as JobRecord[];
    if (raw && Array.isArray(raw.jobs)) return raw.jobs;
    return [];
  }, [jobsQuery.data]);

  // Filter jobs for selected task
  const filteredJobs = useMemo(() => {
    if (!detailTask) return jobs;
    return jobs.filter((j) => j.task === detailTask);
  }, [jobs, detailTask]);

  // Latest results for each task
  const latestQueries = useQueries({
    queries: TASKS.map((task) => ({
      queryKey: ['task-suite-latest', task] as const,
      queryFn: () => client.getTaskSuiteLatest<LatestResult>(task),
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const runTest = useCallback(async () => {
    setRunning(true);
    setRunMessage(null);
    setRunJobId(null);
    setPolling(false);
    try {
      const result = await client.postTaskSuiteRun(selectedTask);
      const id = result.jobId || (result as unknown as Record<string, unknown>).id as string || '';
      setRunJobId(id);
      setRunMessage(`Job started: ${id}`);

      // Poll for completion
      if (id) {
        setPolling(true);
        let attempts = 0;
        const maxAttempts = 60;
        const interval = setInterval(async () => {
          attempts++;
          try {
            const job = await client.getTaskSuiteJob<JobRecord>(id);
            const status = job.status?.toLowerCase() ?? '';
            if (status === 'completed' || status === 'failed' || status === 'cancelled') {
              clearInterval(interval);
              pollingRef.current = null;
              setPolling(false);
              setRunMessage(`Job ${id}: ${job.status}`);
              void qc.invalidateQueries({ queryKey: ['task-suite-jobs'] });
              void qc.invalidateQueries({ queryKey: ['task-suite-latest', selectedTask] });
            } else {
              setRunMessage(`Job ${id}: ${job.status ?? 'running'}… (${attempts})`);
            }
          } catch {
            // Keep polling on transient errors
          }
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            pollingRef.current = null;
            setPolling(false);
            setRunMessage(`Job ${id}: still running (timed out polling)`);
          }
        }, 5000);
        pollingRef.current = interval;
      }
    } catch (e) {
      setRunMessage(`Error: ${(e as Error).message}`);
    } finally {
      setRunning(false);
    }
  }, [client, selectedTask, qc]);

  // Clean up polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  return (
    <div className={styles.container}>
      <Title2>Task Suite</Title2>

      {/* Run Tests */}
      <div className={styles.section}>
        <Text weight="semibold" size={400} className={styles.blockLabelM}>
          Run Tests
        </Text>
        <div className={styles.runControls}>
          <div>
            <Text size={200} className={styles.blockLabel}>Task</Text>
            <Dropdown
              value={selectedTask}
              selectedOptions={[selectedTask]}
              onOptionSelect={(_e, data) => {
                if (data.optionValue) setSelectedTask(data.optionValue as TaskName);
              }}
              className={styles.dropdownTask}
            >
              {TASKS.map((t) => (
                <Option key={t} value={t}>{t}</Option>
              ))}
            </Dropdown>
          </div>
          <Button
            appearance="primary"
            onClick={runTest}
            disabled={running || polling}
            icon={running || polling ? <Spinner size="tiny" /> : <PlayRegular />}
          >
            {running ? 'Starting…' : polling ? 'Running…' : 'Run'}
          </Button>
        </div>
        {runMessage && (
          <div className={styles.runMessageRow} role="alert">
            <Text size={200}>{runMessage}</Text>
          </div>
        )}
      </div>

      {/* Latest Results — derived from most recent job per task */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text weight="semibold" size={400}>Latest Results</Text>
          <Button
            size="small"
            icon={<ArrowClockwise16Regular />}
            onClick={() => {
              qc.invalidateQueries({ queryKey: ['task-suite-jobs'] });
              TASKS.forEach((t) => qc.invalidateQueries({ queryKey: ['task-suite-latest', t] }));
            }}
          >
            Refresh
          </Button>
        </div>
        <div className={styles.cardsGrid}>
          {TASKS.map((task) => {
            const latestJob = jobs
              .filter(j => j.task === task)
              .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0];
            const isSelected = detailTask === task;
            const s = latestJob?.summary;
            const isFailed = latestJob?.status === 'failed';
            const isRunning = latestJob?.status === 'running' || latestJob?.status === 'pending';
            return (
              <Card
                key={task}
                style={{
                  padding: tokens.spacingVerticalM,
                  cursor: 'pointer',
                  outline: isSelected ? `2px solid ${tokens.colorBrandStroke1}` : 'none',
                  backgroundColor: isSelected ? tokens.colorBrandBackground2 : undefined,
                }}
                onClick={() => setDetailTask(isSelected ? null : task)}
              >
                <Text weight="semibold" size={300}>{task}</Text>
                {jobsQuery.isLoading ? (
                  <Spinner size="tiny" />
                ) : latestJob ? (
                  <div className={styles.cardContent}>
                    {isFailed ? (
                      <Badge appearance="tint" color="danger" size="small">failed</Badge>
                    ) : isRunning ? (
                      <Badge appearance="tint" color="informative" size="small">{latestJob.status}</Badge>
                    ) : s ? (
                      <Text size={200} style={{ display: 'block' }}>
                        <span style={{ color: tokens.colorPaletteGreenForeground1 }}>{s.passed ?? 0}</span>
                        {' / '}
                        {s.total ?? 0} passed
                        {s.passRate != null && <Caption1> ({(s.passRate * 100).toFixed(0)}%)</Caption1>}
                      </Text>
                    ) : null}
                    {latestJob.createdAt && (
                      <Text size={200} className={styles.mutedBlock}>
                        {new Date(latestJob.createdAt).toLocaleString()}
                      </Text>
                    )}
                  </div>
                ) : (
                  <Text size={200} className={styles.mutedText}>No data</Text>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Job History — unified table */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text weight="semibold" size={400}>
            {detailTask ? `${detailTask} — Jobs` : 'All Jobs'}
          </Text>
          <div className={styles.buttonGroup}>
            {detailTask && (
              <Button size="small" appearance="subtle" onClick={() => setDetailTask(null)}>
                Show All
              </Button>
            )}
            <Button
              size="small"
              icon={<ArrowClockwise16Regular />}
              onClick={() => qc.invalidateQueries({ queryKey: ['task-suite-jobs'] })}
            >
              Refresh
            </Button>
          </div>
        </div>

        <JobHistoryTable
          jobs={filteredJobs}
          loading={jobsQuery.isLoading}
          emptyMessage={detailTask ? `No jobs found for ${detailTask}.` : 'No jobs found.'}
          latestResults={latestQueries.reduce((acc, q, i) => {
            if (q.data?.results) acc[TASKS[i]] = q.data.results as ResultRow[];
            return acc;
          }, {} as Record<string, ResultRow[]>)}
        />
      </div>
    </div>
  );
}

// Parse summary stats from latest results — handles per-task data structures
function parseSummaryFromLatest(d: LatestResult): { total: number; passed: number; failed: number; disabled: number; passRate: number | null } | null {
  if (!d.results?.length) return null;
  const results = d.results as Record<string, unknown>[];

  // apipath-testing: results have summary.healthy/total — aggregate across all API paths
  if (results.some(r => r.summary)) {
    let total = 0, passed = 0;
    for (const r of results) {
      const s = r.summary as Record<string, number> | undefined;
      if (s) { total += s.total || 0; passed += s.healthy || 0; }
    }
    return { total, passed, failed: total - passed, disabled: 0, passRate: total > 0 ? passed / total : null };
  }

  // failure-testing: results have `passed: true/false`
  if (results.some(r => 'passed' in r)) {
    const total = d.resultCount ?? results.length;
    const passed = results.filter(r => r.passed === true || r.passed === 'True' || r.passed === 'true').length;
    return { total, passed, failed: total - passed, disabled: 0, passRate: total > 0 ? passed / total : null };
  }

  // capabilities-testing / example-testing: results have status field — exclude disabled from totals
  let passed = 0, disabled = 0;
  for (const r of results) {
    const s = ((r.status as string) ?? '').toLowerCase();
    if (s === 'success' || s === 'pass' || s === 'passed') passed++;
    else if (s === 'disabled') disabled++;
  }
  const activeTotal = (d.resultCount ?? results.length) - disabled;
  return { total: activeTotal, passed, failed: activeTotal - passed, disabled, passRate: activeTotal > 0 ? passed / activeTotal : null };
}

// ---- Expandable API Path Results (apipath-testing) ----
function ApiPathResultsTable({ results }: { results: Record<string, unknown>[] }) {
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const toggle = (i: number) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  return (
    <div>
      <Text weight="semibold" size={200} style={{ display: 'block', marginBottom: 4 }}>
        API Path Results ({results.length} paths)
      </Text>
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {results.map((r, i) => {
          const sm = r.summary as Record<string, unknown> | undefined;
          const isActive = sm?.isActive as boolean | undefined;
          const deployments = (r.deployments ?? []) as Array<Record<string, unknown>>;
          const isOpen = expanded.has(i);
          const healthy = sm?.healthy as number ?? 0;
          const total = sm?.total as number ?? 0;
          const failed = total - healthy;

          return (
            <div key={i} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
              <div
                style={{ display: 'grid', gridTemplateColumns: '16px 1fr 70px 70px 70px 90px', alignItems: 'center', gap: 4, padding: '4px 0', cursor: deployments.length > 0 ? 'pointer' : 'default', userSelect: 'none' }}
                onClick={() => deployments.length > 0 && toggle(i)}
              >
                <span style={{ fontSize: 10 }}>{deployments.length > 0 ? (isOpen ? '▾' : '▸') : ' '}</span>
                <Caption1><code>{(r.apiPath as string) || '—'}</code></Caption1>
                <Caption1 style={{ color: tokens.colorPaletteGreenForeground1 }}>{healthy}/{total}</Caption1>
                <Caption1>{sm?.healthPercent != null ? `${(sm.healthPercent as number).toFixed(0)}%` : '—'}</Caption1>
                <Badge appearance="tint" color={isActive ? 'success' : 'danger'} size="small">
                  {isActive ? '✓ Active' : '✗ Down'}
                </Badge>
                {failed > 0 && <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{failed} failed</Caption1>}
              </div>
              {isOpen && deployments.length > 0 && (
                <div style={{ paddingLeft: 20, paddingBottom: 8 }}>
                  <Table size="extra-small" aria-label="Deployment results" style={{ tableLayout: 'fixed' }}>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell style={{ width: '25%' }}>Deployment</TableHeaderCell>
                        <TableHeaderCell style={{ width: '15%' }}>Capability</TableHeaderCell>
                        <TableHeaderCell style={{ width: 60 }}>Status</TableHeaderCell>
                        <TableHeaderCell style={{ width: 50 }}>HTTP</TableHeaderCell>
                        <TableHeaderCell style={{ width: 65 }}>Latency</TableHeaderCell>
                        <TableHeaderCell>Error</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deployments.map((d, di) => {
                        const ok = (d.status as string) === 'success';
                        return (
                          <TableRow key={di}>
                            <TableCell><Caption1><code>{(d.deploymentName as string) || '—'}</code></Caption1></TableCell>
                            <TableCell><Caption1>{(d.capability as string) || '—'}</Caption1></TableCell>
                            <TableCell>
                              <Badge appearance="tint" color={ok ? 'success' : 'danger'} size="small">
                                {ok ? '✓' : (d.status as string) || '✗'}
                              </Badge>
                            </TableCell>
                            <TableCell><Caption1>{d.httpStatus != null ? String(d.httpStatus) : '—'}</Caption1></TableCell>
                            <TableCell><Caption1>{d.responseTimeMs != null ? `${((d.responseTimeMs as number) / 1000).toFixed(1)}s` : '—'}</Caption1></TableCell>
                            <TableCell>
                              <Caption1
                                title={(d.error as string) || ''}
                                style={{
                                  color: d.error ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground3,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical' as const,
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {(d.error as string) || '—'}
                              </Caption1>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Job History Table (ExpandableTable) ----
function JobHistoryTable({ jobs, loading, emptyMessage, latestResults }: {
  jobs: JobRecord[]; loading: boolean; emptyMessage: string;
  latestResults: Record<string, ResultRow[]>;
}) {
  const styles = useStyles();

  const columns: Column<JobRecord>[] = useMemo(() => [
    { key: 'task', label: 'Task', render: (j) => <Text weight="semibold">{j.task ?? '-'}</Text>, getValue: (j) => j.task ?? '', sortable: true, filterable: true, width: '25%' },
    { key: 'status', label: 'Status', render: (j) => <Badge appearance="tint" color={statusColor(j.status)} size="small">{j.status ?? 'Unknown'}</Badge>, getValue: (j) => j.status ?? '', sortable: true, filterable: true },
    { key: 'started', label: 'Started', render: (j) => <Caption1>{j.createdAt ? new Date(j.createdAt).toLocaleString() : '-'}</Caption1>, getValue: (j) => j.createdAt ?? '', sortable: true, hideOnMobile: true },
    { key: 'duration', label: 'Duration', render: (j) => <Caption1>{formatDuration(j.durationSeconds)}</Caption1>, getValue: (j) => formatDuration(j.durationSeconds), hideOnMobile: true },
    { key: 'results', label: 'Results', render: (j) => {
      const s = j.summary;
      if (!s) return <Text>-</Text>;
      return <Text>{s.passed ?? 0}/{s.total ?? 0} passed</Text>;
    }, getValue: (j) => j.summary ? `${j.summary.passed ?? 0}/${j.summary.total ?? 0}` : '', sortable: true },
  ], []);

  const renderExpanded = (job: JobRecord) => {
    const s = job.summary;
    // Only show result details for jobs that completed with results
    const hasResults = s && (s.total ?? 0) > 0;
    const taskResults = hasResults && job.task ? latestResults[job.task] : undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Job summary grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 100px 1fr', gap: '4px 12px' }}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Job ID</Caption1>
          <Caption1 style={{ fontFamily: 'monospace' }}>{job.jobId || job.id || '-'}</Caption1>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Duration</Caption1>
          <Caption1>{formatDuration(job.durationSeconds)}</Caption1>
          {s && <>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Passed</Caption1>
            <Caption1 style={{ color: tokens.colorPaletteGreenForeground1 }}>{s.passed ?? '-'}</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Failed</Caption1>
            <Caption1 style={{ color: s.failed ? tokens.colorPaletteRedForeground1 : undefined }}>{s.failed ?? '-'}</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Pass Rate</Caption1>
            <Caption1>{s.passRate != null ? `${(s.passRate * 100).toFixed(1)}%` : '-'}</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Total</Caption1>
            <Caption1>{s.total ?? '-'}</Caption1>
          </>}
        </div>
        {!hasResults && job.status === 'failed' && (
          <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>Job failed — no results available</Caption1>
        )}
        {taskResults && taskResults.length > 0 && (() => {
          const results = taskResults as Record<string, unknown>[];

          // apipath-testing: show per-API-path summary, expandable to per-deployment details
          if (results.some(r => r.summary)) {
            return <ApiPathResultsTable results={results} />;
          }

          // failure-testing: show per-scenario results
          if (results.some(r => 'passed' in r)) {
            return (
              <div>
                <Text weight="semibold" size={200} style={{ display: 'block', marginBottom: 4 }}>
                  Scenario Results ({results.length} tests)
                </Text>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  <Table size="extra-small" aria-label="Failure test results">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>Scenario</TableHeaderCell>
                        <TableHeaderCell>API Path</TableHeaderCell>
                        <TableHeaderCell style={{ width: 80 }}>Expected</TableHeaderCell>
                        <TableHeaderCell style={{ width: 80 }}>Actual</TableHeaderCell>
                        <TableHeaderCell style={{ width: 80 }}>Result</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.slice(0, 200).map((r, i) => {
                        const passed = r.passed === true || r.passed === 'True' || r.passed === 'true';
                        return (
                          <TableRow key={i}>
                            <TableCell><Caption1>{(r.scenario as string) || '—'}</Caption1></TableCell>
                            <TableCell><Caption1><code>{(r.apiPath as string) || '—'}</code></Caption1></TableCell>
                            <TableCell><Caption1>{(r.expectedStatus as string) ?? '—'}</Caption1></TableCell>
                            <TableCell><Caption1>{(r.actualStatus as string) ?? '—'}</Caption1></TableCell>
                            <TableCell>
                              <Badge appearance="tint" color={passed ? 'success' : 'danger'} size="small">
                                {passed ? '✓' : '✗'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          }

          // capabilities-testing / example-testing: table with status, separate disabled at bottom
          const hasDeployment = results.some(r => 'deployment' in r || 'backend' in r);
          const hasCapability = 'capability' in results[0];
          const hasStatus = 'status' in results[0];
          const hasHttp = results.some(r => 'status_code' in r || 'http_status' in r);
          const hasLatency = results.some(r => 'response_time_ms' in r || 'execution_time_seconds' in r);
          const hasError = results.some(r => 'error_message' in r || 'error' in r);
          const hasDescription = 'description' in results[0];

          if (!hasDeployment && !hasCapability && !hasStatus && !hasDescription) return null;

          const activeResults = results.filter(r => ((r.status as string) ?? '').toLowerCase() !== 'disabled');
          const disabledResults = results.filter(r => ((r.status as string) ?? '').toLowerCase() === 'disabled');

          return (
            <div>
              <Text weight="semibold" size={200} style={{ display: 'block', marginBottom: 4 }}>
                Result Details ({activeResults.length} active{disabledResults.length > 0 ? `, ${disabledResults.length} disabled` : ''})
              </Text>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                <Table size="extra-small" aria-label="Result details" style={{ tableLayout: 'fixed' }}>
                  <TableHeader>
                    <TableRow>
                      {hasDeployment && <TableHeaderCell style={{ width: '20%' }}>Deployment</TableHeaderCell>}
                      {hasCapability && <TableHeaderCell style={{ width: '14%' }}>Capability</TableHeaderCell>}
                      {hasDescription && !hasDeployment && <TableHeaderCell style={{ width: '20%' }}>Description</TableHeaderCell>}
                      {hasStatus && <TableHeaderCell style={{ width: 70 }}>Status</TableHeaderCell>}
                      {hasHttp && <TableHeaderCell style={{ width: 50 }}>HTTP</TableHeaderCell>}
                      {hasLatency && <TableHeaderCell style={{ width: 65 }}>Latency</TableHeaderCell>}
                      {hasError && <TableHeaderCell>Error</TableHeaderCell>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeResults.slice(0, 200).map((r, i) => {
                      const status = (r.status as string) ?? '';
                      const ok = status === 'success' || status === 'pass' || status === 'passed';
                      return (
                        <TableRow key={i}>
                          {hasDeployment && <TableCell><Caption1><code>{(r.deployment as string) || (r.backend as string) || '—'}</code></Caption1></TableCell>}
                          {hasCapability && <TableCell><Caption1>{(r.capability as string) || '—'}</Caption1></TableCell>}
                          {hasDescription && !hasDeployment && <TableCell><Caption1>{(r.description as string) || '—'}</Caption1></TableCell>}
                          {hasStatus && (
                            <TableCell>
                              <Badge appearance="tint" color={ok ? 'success' : 'danger'} size="small">
                                {ok ? '✓' : status || '—'}
                              </Badge>
                            </TableCell>
                          )}
                          {hasHttp && <TableCell><Caption1>{(r.status_code ?? r.http_status) != null ? String(r.status_code ?? r.http_status) : '—'}</Caption1></TableCell>}
                          {hasLatency && (
                            <TableCell>
                              <Caption1>{r.response_time_ms != null ? `${((r.response_time_ms as number) / 1000).toFixed(1)}s` : r.execution_time_seconds != null ? `${(r.execution_time_seconds as number).toFixed(1)}s` : '—'}</Caption1>
                            </TableCell>
                          )}
                          {hasError && (() => {
                            const errText = (r.error_message as string) || (r.error as string) || '';
                            return (
                            <TableCell>
                              <Caption1
                                title={errText}
                                style={{
                                  color: errText ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground3,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical' as const,
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {errText || '—'}
                              </Caption1>
                            </TableCell>
                            );
                          })()}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {disabledResults.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {disabledResults.length} disabled: {disabledResults.slice(0, 10).map(r => `${(r.deployment as string) || (r.backend as string) || ''}/${(r.capability as string) || ''}`).join(', ')}
                    {disabledResults.length > 10 ? ` …and ${disabledResults.length - 10} more` : ''}
                  </Caption1>
                </div>
              )}
            </div>
          );
        })()}

        {!s && (!taskResults || taskResults.length === 0) && (
          <Caption1 className={styles.mutedText}>No details available for this job.</Caption1>
        )}
      </div>
    );
  };

  return (
    <ExpandableTable<JobRecord>
      columns={columns}
      items={jobs}
      getRowKey={(j) => j.jobId || j.id || `${j.task}-${j.createdAt}`}
      renderExpanded={renderExpanded}
      loading={loading}
      emptyMessage={emptyMessage}
      searchPlaceholder="Search jobs…"
      defaultSortKey="started"
      defaultSortDir="desc"
    />
  );
}

 
